import { useState, useCallback } from "react";
import { GetPractitionerRole } from "../../services/fhir";
import { useFhirData } from "../../utils/useFhirData";
import { oids } from "../../configs/Settings";
import { toast } from "react-toastify";

export type AccessRole = "manager" | "practitioner" | "owner" | "associated";

export interface IRole {
  isManager: boolean;
  isOwner: boolean;
  isPractitioner: boolean;
  isAssociated?: boolean;
  associationCnpjDigits?: string;
  organizationId: string;
  organization: any;
  isInvited?: boolean;
}

interface UseRolesReturn {
  roles: IRole[] | undefined;
  loading: boolean;
  fetchUserRoles: (username: string) => Promise<IRole[] | undefined>;
  getRolesArray: (data: IRole) => AccessRole[];
}

export const useRoles = (): UseRolesReturn => {
  const [roles, setRoles] = useState<IRole[]>();
  const [loading, setLoading] = useState(false);

  const getRolesArray = useCallback((data: IRole): AccessRole[] => {
    const roles: AccessRole[] = [];
    if (data?.isManager) roles.push("manager");
    if (data?.isPractitioner) roles.push("practitioner");
    if (data?.isOwner) roles.push("owner");
    if (data?.isAssociated) roles.push("associated");
    return roles;
  }, []);

  const fetchUserRoles = useCallback(async (username: string) => {
    setLoading(true);
    try {
      const practitionerRolesResponse = await GetPractitionerRole({
        practitioner: `${oids.cpf}-${username}`,
        includeOrganization: true,
      }).then((response) => response?.entry);

      const practitionerRoles = practitionerRolesResponse?.filter(
        (e: any) => e.resource.resourceType === "PractitionerRole"
      );
      const organizations = practitionerRolesResponse?.filter(
        (e: any) => e.resource.resourceType === "Organization"
      );

      const verifyRoles = practitionerRoles
        ?.filter((el: any) => {
          const isValidOrganization = organizations
            ?.find(
              (e: any) =>
                e.resource.id ===
                el?.resource?.organization?.reference?.split("/")[1]
            )
            ?.resource?.extension?.find((ext: any) =>
              ext?.url.includes("OrganizationManager")
            );

          return (
            (el?.resource.active === undefined ||
              el?.resource.active !== false) &&
            isValidOrganization
          );
        })
        ?.map((element: any) => useFhirData.extractPracitionerRole(element));

      setRoles(verifyRoles);
      return verifyRoles;
    } catch (error) {
      toast.error("Erro ao carregar funções do usuário");
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return { roles, loading, fetchUserRoles, getRolesArray };
};
